import { handleize, parseConfig, decodeHTMLEntities, parseUrlParametersToActiveFilters } from '@utilities/theme';
import { nostoBuildQuery, nostoSearch, nostoTransformToShopifyFilters, nostoMapFieldToFilter, nostoTransformFilterLabel } from '@utilities/nosto';

/**
 * FilterDrawer - Advanced filter management component extending SlideOutDrawer
 *
 * This component provides comprehensive filter functionality for search and collection pages,
 * supporting both Shopify's native filtering and Nosto's search platform. It handles real-time
 * filter rendering, state management, and seamless communication with ProductGrid components.
 *
 * Key Features:
 * - Dual data source support (Shopify/Nosto) with unified filter interface
 * - Dynamic filter rendering with live count updates
 * - Real-time filter state synchronization with URL parameters
 * - Featured filters integration for sticky filter controls
 * - Browser history support with proper back/forward navigation
 * - Loading and error state management
 *
 * Architecture:
 * - Extends SlideOutDrawer for drawer behavior
 * - Communicates with ProductGrid via custom events
 * - Maintains independent filter state with URL synchronization
 * - Supports both client-side rendering and server-side data
 *
 * Data Flow:
 * 1. Receives filter requests from ProductGrid components
 * 2. Loads filter data from appropriate source (Shopify/Nosto)
 * 3. Renders filters with current state and counts
 * 4. Handles user interactions and dispatches filter change events
 * 5. Updates URL state and synchronizes with other components
 * 6. Manages featured filters for consistent state across UI
 *
 * State Management:
 * - Tracks active filters with parameter-based storage
 * - Handles availability filter special cases
 * - Manages sort preferences and pagination resets
 * - Synchronizes with browser history for shareable URLs
 *
 * @class
 * @extends SlideOutDrawer
 * @customElement
 */
class FilterDrawer extends SlideOutDrawer {
  /**
   * Creates an instance of FilterDrawer
   * @constructor
   */
  constructor() {
    super();
    this.config = {
      leverageUrl: true, // Default to true to maintain existing behavior
    };
    this.elements = {};
    this.state = {
      isLoading: false,
      hasError: false,
      data: null,
      activeFilters: {}, // Initialize as empty object
    };
    this.abortController = null;
    this.filtersRendered = false;
    this.filterInitialized = false;
    this.lastProcessedData = null;

    // Bind event handlers to maintain context for proper cleanup
    this.boundHandlePopState = this.handlePopState.bind(this);
  }

  allowDebug() {
    return this.classList.contains("debug");
  }

  /**
   * Override connectedCallback to add filter-specific initialization
   */
  connectedCallback() {
    // Follow parent class pattern: check if already initialized first
    if (this.isInitialized && this.filterInitialized) {
      if (this.allowDebug()) console.log("FilterDrawer: Already fully initialized, skipping");
      return;
    }

    // Call parent connectedCallback first - this handles moving to body if needed
    super.connectedCallback();

    // If parent decided to move element, it will early exit and trigger connectedCallback again
    // Only proceed with filter-specific initialization if:
    // 1. Parent initialization succeeded (isInitialized = true)
    // 2. We haven't done filter initialization yet (filterInitialized = false)
    if (this.isInitialized && !this.filterInitialized) {
      if (this.allowDebug()) {
        console.log("FilterDrawer: parent initialized, starting filter initialization");
      }
      this.initFilterDrawer();
    } else if (!this.isInitialized) {
      if (this.allowDebug()) {
        console.log("FilterDrawer: parent not initialized, will wait for next connectedCallback");
      }
    } else {
      if (this.allowDebug()) {
        console.log("FilterDrawer: filter already initialized, nothing to do");
      }
    }
  }

  /**
   * Override disconnectedCallback to clean up event listeners
   */
  disconnectedCallback() {
    // Abort any pending requests
    this.abortController?.abort();

    // Clean up event listeners
    window.removeEventListener("popstate", this.boundHandlePopState);

    // Call parent disconnectedCallback if it exists
    if (super.disconnectedCallback) {
      super.disconnectedCallback();
    }
  }

  /**
   * Enhanced initialization for filter drawer functionality
   * Calls parent drawer initialization, then sets up filter-specific features
   */
  initializeDrawer() {
    super.initializeDrawer();
    // Don't call initFilterDrawer here as it's called from connectedCallback
  }

  /**
   * Initialize filter-specific functionality after parent drawer setup
   * Handles configuration parsing, URL state initialization, template setup, and event binding
   * @private
   */
  async initFilterDrawer() {
    // Prevent double initialization
    if (this.filterInitialized) {
      if (this.allowDebug()) {
        console.log("FilterDrawer: Filter already initialized, skipping");
      }
      return;
    }

    try {
      // Parse config from data attributes
      this.config = parseConfig(this, this.allowDebug());

      if (this.allowDebug()) {
        console.log("FilterDrawer config:", this.config);
      }

      // Initialize active filters from URL
      this.initActiveFiltersFromUrl();

      this.initFilterTemplates();
      this.initFilterEvents();
      this.initFeaturedFiltersSync();

      // Mark as initialized to prevent conflicts with facets.js
      this.classList.add("filter-drawer--initialized");

      // Set flag to prevent double initialization
      this.filterInitialized = true;

      if (this.allowDebug()) {
        console.log("FilterDrawer initialized successfully", this);
      }
    } catch (error) {
      console.error("FilterDrawer initialization error:", error);
      this.showErrorState("Failed to initialize filter drawer");
    }
  }

  /**
   * Initialize filter-specific DOM element references
   * @private
   */
  initFilterTemplates() {
    if (this.allowDebug()) {
      console.log("FilterDrawer initFilterTemplates called");
    }

    this.elements.templates = {};
    this.elements.filtersContainer = this.querySelector("[data-filters]");
    this.elements.loadingState = this.querySelector("[data-loading-state]");
    this.elements.errorState = this.querySelector("[data-error-state]");

    if (this.allowDebug()) {
      console.log("FilterDrawer found elements:", {
      filtersContainer: !!this.elements.filtersContainer,
      loadingState: !!this.elements.loadingState,
      errorState: !!this.elements.errorState,
      });
    }

    // Filter templates - use identifier from config
    const identifier = this.config.identifier || "collection-filter-drawer";
    this.elements.templates.filters = document.querySelector(
      `template#filters-template-${identifier}`
    );
    this.elements.templates.sortFilter = document.querySelector(
      `template#sort-filter-template-${identifier}`
    );
    this.elements.templates.sortOption = document.querySelector(
      `template#sort-option-template-${identifier}`
    );
    this.elements.templates.filterGroup = document.querySelector(
      `template#filter-group-template-${identifier}`
    );
    this.elements.templates.filterValue = document.querySelector(
      `template#filter-value-template-${identifier}`
    );
    this.elements.templates.priceRange = document.querySelector(
      `template#price-range-template-${identifier}`
    );

    if (this.allowDebug()) {
      console.log("FilterDrawer found templates:", {
      identifier: identifier,
      filters: !!this.elements.templates.filters,
      sortFilter: !!this.elements.templates.sortFilter,
      sortOption: !!this.elements.templates.sortOption,
      filterGroup: !!this.elements.templates.filterGroup,
      filterValue: !!this.elements.templates.filterValue,
      priceRange: !!this.elements.templates.priceRange,
      templateSelectors: {
        filters: `template#filters-template-${identifier}`,
        sortFilter: `template#sort-filter-template-${identifier}`,
        filterGroup: `template#filter-group-template-${identifier}`,
        filterValue: `template#filter-value-template-${identifier}`,
      },
      });
    }

    // Check for missing critical elements
    if (!this.elements.filtersContainer) {
      console.error("FilterDrawer: Missing filters container element [data-filters]");
      throw new Error("Filter drawer requires a container element with [data-filters] attribute");
    }

    if (!this.elements.templates.filters) {
      console.error(`FilterDrawer: Missing filters template #filters-template-${identifier}`);
      console.error("Available templates in DOM:", Array.from(document.querySelectorAll('template')).map(t => t.id));
      throw new Error(`Filter drawer requires a template with id 'filters-template-${identifier}'`);
    }
  }

  /**
   * Initialize filter-specific event listeners
   * @private
   */
  initFilterEvents() {
    // Listen for drawer open events to load and render filters
    this.addEventListener(
      "slide-out-drawer:open",
      this.handleDrawerOpen.bind(this)
    );

    // Listen for filter change events from product grids
    document.addEventListener(
      "product-grid:filter-request",
      this.handleFilterRequest.bind(this)
    );

    // Listen for updated filter data from product grids
    document.addEventListener(
      "product-grid:data-updated",
      this.handleProductGridDataUpdate.bind(this)
    );

    // Listen for browser back/forward navigation to sync filter UI
    window.addEventListener(
      "popstate",
      this.boundHandlePopState
    );

    // Listen for facet remove events (from simplified FacetRemove component)
    document.addEventListener(
      "facet-remove:clicked",
      this.handleFacetRemoveClick.bind(this)
    );

    // Listen for vendor changes from theme utilities
    document.addEventListener(
      "vendor:changed",
      this.handleVendorChange.bind(this)
    );
  }

  /**
   * Handle drawer open event
   * @param {CustomEvent} event
   * @private
   */
  async handleDrawerOpen(event) {
    if (this.allowDebug()) {
      console.log("FilterDrawer drawer opened", {
      event,
      drawer: this,
      config: this.config,
      shouldAutoLoad: this.shouldAutoLoad(),
      filtersRendered: this.filtersRendered,
      elements: {
        filtersContainer: !!this.elements.filtersContainer,
        loadingState: !!this.elements.loadingState,
        errorState: !!this.elements.errorState,
        templates: Object.keys(this.elements.templates || {}).reduce(
          (acc, key) => {
            acc[key] = !!this.elements.templates[key];
            return acc;
          },
          {}
        ),
      },
      });
    }

    // Only render filters if they haven't been rendered before
    if (!this.filtersRendered) {
      if (this.shouldAutoLoad()) {
        if (this.allowDebug()) {
          console.log("FilterDrawer: Auto-loading filters");
        }
        this.showLoadingState();
        try {
          await this.loadFilterData();
          if (this.state.data && this.state.data.filters) {
            if (this.allowDebug()) {
              console.log("FilterDrawer: Rendering filters with data");
            }
            this.renderFilters(this.state.data.filters);
          } else {
            if (this.allowDebug()) {
              console.log("FilterDrawer: No filter data available, rendering empty state");
            }
            this.renderEmptyFilters();
          }
        } catch (error) {
          console.error("FilterDrawer: Failed to load filter data:", error);
          this.showErrorState("Failed to load filters");
        }
      } else {
        if (this.allowDebug()) {
          console.log("FilterDrawer: Not auto-loading, rendering empty filters");
        }
        // Show loading briefly before rendering empty state
        this.showLoadingState();
        setTimeout(() => {
          this.renderEmptyFilters();
        }, 100);
      }
    } else {
      if (this.allowDebug()) {
        console.log("FilterDrawer: Filters already rendered, showing existing content");
      }
      // Ensure loaded state is shown
      this.setLoadedState();
      // Update active filters state in case URL has changed
      this.updateActiveFiltersState();
    }
  }

  /**
   * Handle filter request from product grids
   * @param {CustomEvent} event
   * @private
   */
  async handleFilterRequest(event) {
    const { config } = event.detail;

    // Update our config with the request details
    this.config = { ...this.config, ...config };

    // Reset filters rendered flag to force re-render
    this.filtersRendered = false;

    if (this.isDrawerOpen()) {
      // If drawer is open, immediately load and render
      this.showLoadingState();
      try {
        await this.loadFilterData();
        if (this.state.data && this.state.data.filters) {
          this.renderFilters(this.state.data.filters);
        } else {
          this.renderEmptyFilters();
        }
      } catch (error) {
        console.error("Failed to load filter data:", error);
        this.showErrorState();
      }
    }
  }

  /**
   * Handle product grid data update
   * @param {CustomEvent} event
   * @private
   */
  handleProductGridDataUpdate(event) {
    const { data } = event.detail;

    if (this.allowDebug()) {
      console.log("FilterDrawer received data update:", data);
    }

    // Prevent redundant processing of the same data
    if (this.lastProcessedData === data) {
      if (this.allowDebug()) {
        console.log("FilterDrawer: Same data already processed, skipping");
      }
      return;
    }

    this.lastProcessedData = data;

    // Only update if we have filters rendered and new filter data available
    if (this.filtersRendered && data && data.filters) {
      this.updateExistingFilters(data.filters);
    } else if (data && data.filters) {
      // If filters not rendered yet, render them
      this.renderFilters(data.filters);
    } else if (this.filtersRendered) {
      // If no filter data but filters were rendered, update to empty state
      this.updateFiltersToEmptyState();
    }
  }

  /**
   * Handle vendor change events from theme utilities
   * @param {CustomEvent} event
   * @private
   */
  async handleVendorChange(event) {
    if (this.allowDebug()) {
      console.log("handleVendorChange", event);
    }

    const { vendor } = event.detail;

    if (this.allowDebug()) {
      console.log(`FilterDrawer: Search vendor changed to ${vendor}, reloading filter data...`);
    }

    // Reset filters rendered flag to force re-render with new vendor data
    this.filtersRendered = false;

    // If drawer is currently open, reload filter data immediately
    if (this.isDrawerOpen()) {
      try {
        this.showLoadingState();
        await this.loadFilterData();
        if (this.state.data && this.state.data.filters) {
          this.renderFilters(this.state.data.filters);
        } else {
          this.renderEmptyFilters();
        }
      } catch (error) {
        console.error("FilterDrawer: Failed to reload filter data after vendor change:", error);
        this.showErrorState("Failed to load filters");
      }
    }
  }

  /**
   * Handle browser back/forward navigation
   * @param {PopStateEvent} event
   * @private
   */
  handlePopState(event) {
    if (this.allowDebug()) {
      console.log("FilterDrawer handlePopState", event);
    }

    // Only handle popstate if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      if (this.allowDebug()) {
        console.log("FilterDrawer: leverageUrl is false, ignoring popstate event");
      }
      return;
    }

    // Re-initialize active filters from current URL
    this.initActiveFiltersFromUrl();

    // Update the filter drawer UI to reflect current URL state
    this.updateActiveFiltersState();

    // Update the filter drawer UI controls if filters are rendered
    if (this.filtersRendered) {
      this.updateFilterDrawerUI();
    }
  }

  /**
   * Handle facet remove click events
   * @param {CustomEvent} event
   * @private
   */
  handleFacetRemoveClick(event) {
    if (this.allowDebug()) {
      console.log("FilterDrawer handleFacetRemoveClick", event);
    }

    const originalEvent = event.detail.originalEvent;
    const link = originalEvent.target.closest('a');

    if (link && link.href) {
      // Extract URL parameters from the clear filter link
      const url = new URL(link.href, window.location.origin);
      const searchParams = url.search.slice(1);

      if (this.allowDebug()) {
        console.log("FilterDrawer: Processing facet remove", { href: link.href, searchParams });
      }
    }
  }

  /**
   * Update existing filter elements in the DOM with new filter data
   * Efficiently updates counts, states, and availability without full re-render
   * @param {Array} newFilters - Updated filter data from search/collection response
   * @private
   */
  updateExistingFilters(newFilters) {
    if (!this.elements.filtersContainer) {
      return;
    }

    const container = this.elements.filtersContainer;
    const filterGroupsContainer = container.querySelector("[data-filters-container]") || container;

    // Create a map of new filter data for quick lookup
    const filterMap = new Map();
    newFilters.forEach(filter => {
      filterMap.set(filter.paramName, filter);
    });

    if (this.allowDebug()) {
      console.log("updateExistingFilters: Available new filters:", newFilters.map(f => ({
        label: f.label,
        paramName: f.paramName,
        handleized: handleize(f.label)
      })));
    }

    // Update each existing filter group
    const filterGroups = container.querySelectorAll("details[data-facet]");

    if (this.allowDebug()) {
      console.log("updateExistingFilters: Existing filter groups in DOM:", Array.from(filterGroups).map(group => group.getAttribute("data-facet")));
    }
    const existingFacetNames = new Set();
    filterGroups.forEach(group => {
      const facetName = group.getAttribute("data-facet");

      // Skip sort-by group as it doesn't need count updates
      if (facetName === "sort-by") {
        return;
      }
      existingFacetNames.add(facetName);

      if (this.allowDebug()) {
        console.log(`updateExistingFilters: Looking for match for facet "${facetName}"`);
      }

      // Find the corresponding filter in the new data by matching paramName
      const matchingFilter = filterMap.get(facetName);

      if (matchingFilter) {
        if (this.allowDebug()) {
          console.log(`updateExistingFilters: ✓ Found matching filter for "${facetName}":`, matchingFilter.label);
        }
        this.updateFilterGroup(group, matchingFilter);
      } else {
        // If filter group no longer exists in new data, disable all its values
        if (this.allowDebug()) {
          console.log(`updateExistingFilters: ✗ No matching filter found for facet "${facetName}", disabling group`);
        }
        this.disableFilterGroup(group);
      }
    });

    // Add any new filter groups that are present in response but missing from DOM.
    newFilters.forEach((filter) => {
      if (!filter?.paramName || existingFacetNames.has(filter.paramName)) {
        return;
      }

      if (this.allowDebug()) {
        console.log(`updateExistingFilters: Adding new filter group "${filter.paramName}"`);
      }

      this.renderFilterGroup(filterGroupsContainer, filter);
    });

    // Update active filters state
    this.updateActiveFiltersState();
  }

  /**
   * Update an existing filter group with new data
   * @param {HTMLElement} groupElement - The filter group DOM element
   * @param {Object} filterData - New filter data
   * @private
   */
  updateFilterGroup(groupElement, filterData) {
    let filterInputs = Array.from(groupElement.querySelectorAll("[data-filter-input]"));
    const valuesContainer = groupElement.querySelector("[data-filter-values]");
    const incomingValues = Array.isArray(filterData.values) ? filterData.values : [];

    // Create a map of new filter values for quick lookup
    const valueMap = new Map();
    if (incomingValues.length > 0) {
      incomingValues.forEach(value => {
        valueMap.set(String(value.value), value);
      });
    }

    if (this.allowDebug()) {
      console.log(`Updating filter group with data:`, {
        groupElement: groupElement.getAttribute("data-facet"),
        filterData,
        valueMapKeys: Array.from(valueMap.keys()),
        inputValues: Array.from(filterInputs).map(input => input.dataset.filterValue)
      });
    }

    // If the value set changed (added/removed options), rebuild this group's values in place.
    // This is still cheaper than a full drawer re-render and avoids stale/hidden options.
    const existingValueSet = new Set(filterInputs.map((input) => String(input.dataset.filterValue)));
    const incomingValueSet = new Set(incomingValues.map((value) => String(value.value)));
    const hasStructuralChanges =
      existingValueSet.size !== incomingValueSet.size ||
      Array.from(incomingValueSet).some((value) => !existingValueSet.has(value)) ||
      Array.from(existingValueSet).some((value) => !incomingValueSet.has(value));

    if (hasStructuralChanges && valuesContainer) {
      if (this.allowDebug()) {
        console.log(`updateFilterGroup: Rebuilding values for "${filterData.paramName}" due to structural changes`);
      }

      valuesContainer.innerHTML = "";
      const sortedValues = this.sortFilterValues(filterData, incomingValues);
      sortedValues.forEach((value) => {
        this.renderFilterValue(valuesContainer, filterData, value);
      });
      filterInputs = Array.from(groupElement.querySelectorAll("[data-filter-input]"));
    } else {
    // Update each filter input based on new data
    filterInputs.forEach(input => {
      const filterValue = input.dataset.filterValue;
      const newValueData = valueMap.get(String(filterValue));

      if (newValueData) {
        this.updateFilterInput(input, newValueData);
      } else {
        // Value no longer exists in new data, disable it
        if (this.allowDebug()) {
          console.log(`Filter value '${filterValue}' not found in new data, disabling input`);
        }
        this.disableFilterInput(input);
      }
    });
    }

    // Check if entire group should be enabled or disabled
    const enabledInputs = groupElement.querySelectorAll("[data-filter-input]:not([disabled])");
    const isGroupDisabled = groupElement.classList.contains("facets__disclosure--disabled");

    if (enabledInputs.length === 0 && !isGroupDisabled) {
      // Group should be disabled
      this.disableFilterGroup(groupElement);
    } else if (enabledInputs.length > 0 && isGroupDisabled) {
      // Group should be enabled
      this.enableFilterGroup(groupElement);
    }

    // Update selected count for this group
    this.updateGroupSelectedCount(groupElement);
  }

  /**
   * Update a specific filter input with new data
   * @param {HTMLElement} input - The filter input element
   * @param {Object} valueData - New value data
   * @private
   */
  updateFilterInput(input, valueData) {
    const label = input.closest('label') || input.parentElement.querySelector('label');
    const filterName = input.dataset.filterName;
    const filterValue = input.dataset.filterValue;

    // Special handling for availability filters
    const isInstockParam =
      filterName === "filter.v.m.greyson.availability" && filterValue === "1";

    // Update disabled state based on count and active status
    // Enable input if it has count > 0, is currently active, or is an availability filter
    const shouldDisable = valueData.count === 0 && !valueData.active && !isInstockParam;
    input.disabled = shouldDisable;

    // Update label classes
    if (label) {
      // Update data attribute with new count and active status
      label.setAttribute(
        "data-extra-info",
        `${valueData.count || 0}-${valueData.active || false}`
      );

      // Update disabled class
      if (shouldDisable) {
        label.classList.add("facet-checkbox--disabled");
      } else {
        label.classList.remove("facet-checkbox--disabled");
        // Also remove any opacity styling that might have been applied
        label.style.opacity = "";
      }

      // Update active class based on current checked state
      if (input.checked) {
        label.classList.add("active");
      } else {
        label.classList.remove("active");
      }
    }

    if (this.allowDebug()) {
      console.log(`Updated filter input: ${filterName} = ${filterValue}`, {
        count: valueData.count,
        active: valueData.active,
        disabled: shouldDisable,
        checked: input.checked
      });
    }
  }

  /**
   * Disable a filter input (when value no longer exists in new data)
   * @param {HTMLElement} input - The filter input element
   * @private
   */
  disableFilterInput(input) {
    const label = input.closest('label') || input.parentElement.querySelector('label');

    // Disable the input unless it's currently checked (active)
    if (!input.checked) {
      input.disabled = true;

      if (label) {
        label.classList.add("facet-checkbox--disabled");
        label.setAttribute("data-extra-info", "0-false");
      }
    }

    if (this.allowDebug()) {
      console.log(`Disabled filter input: ${input.dataset.filterName} = ${input.dataset.filterValue}`);
    }
  }

  /**
   * Disable an entire filter group when no values are available
   * @param {HTMLElement} groupElement - The filter group DOM element
   * @private
   */
  disableFilterGroup(groupElement) {
    const filterInputs = groupElement.querySelectorAll("[data-filter-input]");

    // Disable all individual filter inputs
    filterInputs.forEach(input => {
      this.disableFilterInput(input);
    });

    // Disable the group header if all inputs are disabled
    const enabledInputs = groupElement.querySelectorAll("[data-filter-input]:not([disabled])");
    if (enabledInputs.length === 0) {
      // Add disabled class to the filter label
      const filterLabel = groupElement.querySelector("[data-filter-label]");
      if (filterLabel) {
        filterLabel.classList.add("facet-checkbox--disabled");
        filterLabel.style.opacity = "0.4";
      }

      // Hide the arrow SVG
      const arrowSvg = groupElement.querySelector("summary svg");
      if (arrowSvg) {
        arrowSvg.style.display = "none";
      }

      // Prevent the details element from opening
      groupElement.style.pointerEvents = "none";

      // Add a class for styling purposes
      groupElement.classList.add("facets__disclosure--disabled");

      if (this.allowDebug()) {
        console.log(`Fully disabled filter group: ${groupElement.getAttribute("data-facet")}`);
      }
    }

    if (this.allowDebug()) {
      console.log(`Disabled filter group: ${groupElement.getAttribute("data-facet")}`);
    }
  }

  /**
   * Update the selected count display for a filter group
   * @param {HTMLElement} groupElement - The filter group DOM element
   * @private
   */
  updateGroupSelectedCount(groupElement) {
    const selectedCountElement = groupElement.querySelector("[data-selected-count]");
    if (!selectedCountElement) {
      return;
    }

    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    // Count checked inputs, excluding availability filters
    const checkedInputs = groupElement.querySelectorAll("[data-filter-input]:checked");
    let activeCount = 0;

    checkedInputs.forEach(input => {
      const filterName = input.dataset.filterName;
      if (!availabilityFilters.includes(filterName)) {
        activeCount++;
      }
    });

    if (activeCount > 0) {
      selectedCountElement.textContent =
        window.theme?.locales?.filters_selected?.replace(
          "{{ count }}",
          activeCount
        ) || `${activeCount} selected`;
      selectedCountElement.setAttribute("data-selected-count", activeCount);
      selectedCountElement.style.display = "";
    } else {
      selectedCountElement.style.display = "none";
    }
  }

  /**
   * Update filters to empty state when no data is available
   * @private
   */
  updateFiltersToEmptyState() {
    if (!this.elements.filtersContainer) {
      return;
    }

    const container = this.elements.filtersContainer;
    const filterGroups = container.querySelectorAll("details[data-facet]");

    // Disable all filter groups except sort
    filterGroups.forEach(group => {
      const facetName = group.getAttribute("data-facet");
      if (facetName !== "sort-by") {
        this.disableFilterGroup(group);
      }
    });

    if (this.allowDebug()) {
      console.log("Updated filters to empty state");
    }
  }

  /**
   * Determine if component should auto-load data
   * @returns {boolean}
   * @private
   */
  shouldAutoLoad() {
    if (this.config.context === "search") {
      return this.config.terms && this.config.terms.trim().length > 0;
    } else if (this.config.context === "collection") {
      return this.config.handle || this.config.id;
    }
    return false;
  }

  /**
   * Load filter data from appropriate source
   * @returns {Promise<void>}
   */
  async loadFilterData() {
    if (this.allowDebug()) {
      console.log("loadFilterData", this);
    }
    if (this.state.isLoading) return;

    this.setState({ isLoading: true, hasError: false });

    try {
      // Abort any existing request
      this.abortController?.abort();
      this.abortController = new AbortController();

      const vendor = window.theme?.data?.source || "shopify";
      let data;

      if (this.allowDebug()) {
        console.log("fetching filter data from", vendor);
      }

      if (vendor === "nosto") {
        data = await this.fetchNostoData();
      } else {
        data = await this.fetchShopifyData();
      }

      this.setState({ data, isLoading: false });
    } catch (error) {
      if (error.name !== "AbortError") {
        console.error("FilterDrawer data loading error:", error);
        this.setState({ hasError: true, isLoading: false });

        // Use user-friendly error message if available, fallback to generic message
        let errorMessage = error.userMessage || error.message || "Unable to load filters. Please try again.";

        // Log technical details for debugging while showing user-friendly message
        if (this.allowDebug()) {
          console.error("FilterDrawer technical error details:", {
            code: error.code,
            userMessage: error.userMessage,
            debugMessage: error.debugMessage,
            originalError: error.originalError,
            details: error.details
          });
        }

        this.showErrorState(errorMessage);
      }
    }
  }

  /**
   * Fetch data from Shopify
   * @returns {Promise<Object>}
   * @private
   */
  async fetchShopifyData() {
    const url = this.buildShopifyUrl();

    const response = await fetch(url, {
      signal: this.abortController.signal
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const text = await response.text();
    return JSON.parse(text);
  }

  /**
   * Fetch filter data from Nosto using the utilities system.
   *
   * Builds a filter-only query (type: "filters") with minimal size to get
   * available filter options without loading full product data.
   * Transforms the response to Shopify filter format.
   *
   * @returns {Promise<Object>} Shopify-compatible filter data with sort options
   * @throws {Error} When Nosto search fails
   * @private
   */
  async fetchNostoData() {
    try {
      const queryConfig = nostoBuildQuery({
        context: this.config.context,
        terms: this.config.terms,
        categoryId: this.config.id,
        type: "filters", // Only need filters, not full product data
        size: 1, // Minimal size for filter-only queries
        from: 0,
        filters: [], // No filters for getting available filter options
        sortBy: this.config.sortBy || this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance"),
        allowSoldOutStateProducts: this.config.allowSoldOutStateProducts || false,
      });

      const response = await nostoSearch(
        queryConfig.query,
        queryConfig.options,
        { debug: this.allowDebug(), trigger: 'filter-drawer' }
      );

      return nostoTransformToShopifyFilters(response, {
        sortBy: this.config.sortBy || this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance"),
        getSortOptions: this.getSortOptions.bind(this),
        urlBuilder: this.buildFilterUrl.bind(this)
      });
    } catch (error) {
      throw new Error(`Nosto search failed: ${error.message}`);
    }
  }

  /**
   * Build filter URL for add/remove actions
   * @param {string} nostoFieldOrName - Nosto field name or display name
   * @param {string} filterValue
   * @param {string} action - 'add' or 'remove'
   * @returns {string}
   * @private
   */
  buildFilterUrl(nostoFieldOrName, filterValue, action) {
    const currentUrl = new URL(window.location);
    const paramName = nostoMapFieldToFilter(nostoFieldOrName) || nostoFieldOrName;

    let params;

    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      // Build params from internal state instead of URL
      params = new URLSearchParams();
      if (this.state.activeFilters) {
        Object.entries(this.state.activeFilters).forEach(([key, values]) => {
          if (values && values.length > 0) {
            values.forEach(val => params.append(key, val));
          }
        });
      }
    } else {
      params = new URLSearchParams(currentUrl.search);
    }

    if (action === "add") {
      params.append(paramName, filterValue);
    } else {
      const values = params.getAll(paramName);
      params.delete(paramName);
      values
        .filter((v) => v !== filterValue)
        .forEach((v) => params.append(paramName, v));
    }

    return `${currentUrl.pathname}?${params.toString()}`;
  }

  /**
   * Build Shopify API URL based on context
   * @returns {string}
   * @private
   */
  buildShopifyUrl() {
    const baseUrl = window.location.origin;
    let path;
    const params = new URLSearchParams();

    if (this.config.context === "search") {
      path = "/search";
      if (this.config.terms) params.set("q", this.config.terms);
      if (this.config.type) params.set("type", this.config.type);
    } else if (this.config.context === "collection") {
      path = `/collections/${this.config.handle}`;
    }

    // Add common parameters
    params.set("view", "csr-json");

    // Add active filters
    this.addActiveFiltersToParams(params);

    // Add sort parameter
    if (this.config.sortBy) params.set("sort_by", this.config.sortBy);

    return `${baseUrl}${path}?${params.toString()}`;
  }

  /**
   * Add active filters to URL parameters (for Shopify)
   * @param {URLSearchParams} params
   * @private
   */
  addActiveFiltersToParams(params) {
    if (this.state.activeFilters && Object.keys(this.state.activeFilters).length > 0) {
      Object.entries(this.state.activeFilters).forEach(([key, values]) => {
        if(values && values.length > 0) {
        values.forEach((value) => {
            params.append(key, value);
          });
        }
      });
    }
  }

  /**
   * Get sort options based on context
   * @returns {Array}
   * @private
   */
  getSortOptions() {
    if (this.config.context === "search") {
      // Search context: Only relevance and price sorting
      return [
        ["relevance", "Relevance"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    } else if (this.config.context === "collection") {
      // Collection context: Featured, best selling, and price sorting
      return [
        ["manual", "Featured"],
        ["best-selling", "Best Selling"],
        ["price-ascending", "Price, low to high"],
        ["price-descending", "Price, high to low"],
      ];
    }

    // Fallback for other contexts
    return [
      ["relevance", "Relevance"],
      ["price-ascending", "Price, low to high"],
      ["price-descending", "Price, high to low"],
    ];
  }

  /**
   * Render filters in the drawer
   * @param {Array} filters
   * @private
   */
  renderFilters(filters) {
    if (this.allowDebug()) {
      console.log("rendering filters", filters);
    }

    if (!this.elements.filtersContainer || !this.elements.templates.filters) {
      console.error("Filter container or template not found");
      return;
    }

    const template = this.elements.templates.filters;
    const container = this.elements.filtersContainer;

    // Set loaded state
    this.setLoadedState();

    // Clear existing content
    container.innerHTML = "";

    // Clone main template
    const templateClone = template.content.cloneNode(true);

    // Set collection title
    const titleElement = templateClone.querySelector("[data-collection-title]");
    if (titleElement) {
      const title = this.getDisplayTitle();
      titleElement.textContent = title;
    }

    // Calculate total active filters and set data attribute, excluding availability filters
    let totalActiveFilters = 0;
    const availabilityFilterParams = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    if (filters && filters.length > 0) {
      filters.forEach((filter) => {
        if (filter.activeValues && filter.activeValues.length > 0 &&
            !availabilityFilterParams.includes(filter.paramName)) {
          totalActiveFilters += filter.activeValues.length;
        }
      });
    }

    // Set data attribute for CSS to handle visibility
    this.dataset.activeFilters = totalActiveFilters > 0 ? "true" : "false";

    // Update total selected display text only
    const totalSelectedElement = templateClone.querySelector(
      "[data-total-selected]"
    );
    if (totalSelectedElement && totalActiveFilters > 0) {
      totalSelectedElement.textContent =
        window.theme?.locales?.filters_selected?.replace(
          "{{ count }}",
          totalActiveFilters
        ) || `${totalActiveFilters} selected`;
    }

    // Get filters container
    const filtersContainer = templateClone.querySelector(
      "[data-filters-container]"
    );
    if (!filtersContainer) {
      container.appendChild(templateClone);
      return;
    }

    // Render sort options
    this.renderSortOptions(filtersContainer);

    // Render filter groups
    if (filters && filters.length > 0) {
      if (this.allowDebug()) {
        console.log(`renderFilters: About to render ${filters.length} filter groups:`, filters);
      }
      filters.forEach((filter, index) => {
        if (this.allowDebug()) {
          console.log(`renderFilters: Processing filter ${index}:`, filter);
        }
        this.renderFilterGroup(filtersContainer, filter);
      });
    } else {
      if (this.allowDebug()) {
        console.log("renderFilters: No filters to render", { filters, hasFilters: !!filters, filtersLength: filters ? filters.length : 'N/A' });
      }
    }

    // Set up event listeners
    this.setupFilterEvents(templateClone);

    // Append to container
    container.appendChild(templateClone);

    // Mark filters as rendered
    this.filtersRendered = true;

    // Update filter UI to reflect active filters from URL
    this.updateFilterDrawerUI();

    // Set up close button event listeners after filters are rendered
    this.setupCloseButtonListeners();
  }

  /**
   * Render empty filter state
   * @private
   */
  renderEmptyFilters() {
    if (this.allowDebug()) {
      console.log("renderEmptyFilters called");
      console.log("filtersContainer:", this.elements.filtersContainer);
      console.log("filters template:", this.elements.templates.filters);
    }

    if (!this.elements.filtersContainer || !this.elements.templates.filters) {
      if (this.allowDebug()) {
        console.log(
          "Missing filtersContainer or filters template, returning early"
        );
      }
      return;
    }

    const template = this.elements.templates.filters;
    const container = this.elements.filtersContainer;

    // Set loaded state
    this.setLoadedState();

    // Clear existing content
    container.innerHTML = "";

    // Clone main template
    const templateClone = template.content.cloneNode(true);

    // Set collection title
    const titleElement = templateClone.querySelector("[data-collection-title]");
    if (titleElement) {
      const title = this.getDisplayTitle() || "Search";
      titleElement.textContent = title;
      if (this.allowDebug()) {
        console.log("Set collection title to:", title);
      }
    }

    // Set data attribute for empty state (no active filters)
    this.dataset.activeFilters = "false";

    // Get filters container
    const filtersContainer = templateClone.querySelector(
      "[data-filters-container]"
    );
    if (!filtersContainer) {
      if (this.allowDebug()) {
        console.log(
          "No filters container found in template, appending template directly"
        );
      }
      container.appendChild(templateClone);
      return;
    }

    // Only render sort options for empty state
    if (this.allowDebug()) {
      console.log("Rendering sort options for empty state");
    }
    this.renderSortOptions(filtersContainer);

    // Set up event listeners
    this.setupFilterEvents(templateClone);

    // Append to container
    container.appendChild(templateClone);

    // Mark filters as rendered
    this.filtersRendered = true;

    // Update filter UI to reflect active filters from URL
    this.updateFilterDrawerUI();

    if (this.allowDebug()) {
      console.log("Empty filters rendered successfully");
    }

    // Set up close button event listeners after filters are rendered
    this.setupCloseButtonListeners();
  }

  /**
   * Render sort options
   * @param {HTMLElement} container
   * @private
   */
  renderSortOptions(container) {
    if (
      !this.elements.templates.sortFilter ||
      !this.elements.templates.sortOption
    ) {
      return;
    }

    const sortTemplate = this.elements.templates.sortFilter;
    const sortClone = sortTemplate.content.cloneNode(true);

    const sortOptionsContainer = sortClone.querySelector("[data-sort-options]");
    const activeSortElement = sortClone.querySelector("[data-active-sort]");

    if (!sortOptionsContainer) {
      container.appendChild(sortClone);
      return;
    }

    const sortOptions = this.getSortOptions();
    const defaultSort = this.config.defaultSortBy || (this.config.context === "collection" ? "manual" : "relevance");
    const currentSort = this.config.sortBy || defaultSort;
    const ctxPrefix = this.config.contextPrefix || "filters";
    const identifier = this.config.identifier || "filter-drawer";

    sortOptions.forEach(([value, label]) => {

      // Transform labels to match server-side display
      let displayLabel = label;
      if (value === "manual") {
        displayLabel = "Featured";
      } else if (value === "best-selling") {
        displayLabel = "Best Selling";
      } else if (value === "price-ascending") {
        displayLabel = "Price - Low To High";
      } else if (value === "price-descending") {
        displayLabel = "Price - High To Low";
      }

      const optionTemplate = this.elements.templates.sortOption;
      const optionClone = optionTemplate.content.cloneNode(true);

      const input = optionClone.querySelector("[data-sort-input]");
      const nameElement = optionClone.querySelector("[data-sort-name]");
      const labelElement = optionClone.querySelector("label");

      if (input) {
        input.value = value;
        input.id = `sort--${ctxPrefix}--${identifier}--${label.replace(/[^a-zA-Z0-9]/g, "-")}-${
          sortOptionsContainer.children.length + 1
        }`;
        input.checked = value === currentSort;
      }

      if (labelElement) {
        labelElement.setAttribute("for", input?.id);
        labelElement.classList.add("facet-checkbox");
        if (value === currentSort) {
          labelElement.classList.add("facet-active");
        }
      }

      if (nameElement) {
        nameElement.textContent = displayLabel;
        nameElement.className = value;
      }

      sortOptionsContainer.appendChild(optionClone);
    });

    // Update active sort display
    if (activeSortElement) {
      let activeSortLabel = "Relevance";
      if (currentSort === "manual") {
        activeSortLabel = "Featured";
      } else if (currentSort === "best-selling") {
        activeSortLabel = "Best Selling";
      } else if (currentSort === "price-ascending") {
        activeSortLabel = "Price - Low To High";
      } else if (currentSort === "price-descending") {
        activeSortLabel = "Price - High To Low";
      } else {
        const activeOption = sortOptions.find(
          ([value]) => value === currentSort
        );
        if (activeOption) {
          activeSortLabel = activeOption[1];
        }
      }
      activeSortElement.textContent = activeSortLabel;
    }

    container.appendChild(sortClone);
  }

  /**
   * Render filter group
   * @param {HTMLElement} container
   * @param {Object} filter
   * @private
   */
    renderFilterGroup(container, filter) {
    if (this.allowDebug()) {
      console.log("renderFilterGroup called with filter:", filter);
      console.log("Filter label:", filter.label);
      console.log("Filter name:", filter.name);
      console.log("Filter values count:", filter.values ? filter.values.length : (filter.data ? filter.data.length : 'no values'));
      console.log("Filter values:", filter.values);
      console.log("Filter data:", filter.data);
      console.log("Filter expected Shopify format - has label?", !!filter.label, "has values?", !!filter.values, "has paramName?", !!filter.paramName);
    }

    // Skip filters that should be excluded or have insufficient values
    if (!filter.values || filter.values.length <= 1) {
      if (this.allowDebug()) {
        console.log(`Skipping filter "${filter.label || filter.name}" - insufficient values (${filter.values ? filter.values.length : 0})`);
        console.log("Filter data indicates this is likely raw Nosto format that wasn't transformed to Shopify format");
      }
      return;
    }

    // Skip filters that are in the filtersToExclude configuration
    if (this.config.filtersToExclude && Array.isArray(this.config.filtersToExclude)) {
      const filterLabel = filter.label || filter.name;
      const filterParamName = filter.paramName;

      if (this.config.filtersToExclude.includes(filterLabel) ||
          (filterParamName && this.config.filtersToExclude.includes(filterParamName))) {
        if (this.allowDebug()) {
          console.log(`Skipping filter "${filterLabel}" (paramName: "${filterParamName}") - found in filtersToExclude configuration`);
        }
        return;
      }
    }

    if (
      !this.elements.templates.filterGroup ||
      !this.elements.templates.filterValue
    ) {
      return;
    }

    const template = this.elements.templates.filterGroup;
    const clone = template.content.cloneNode(true);

    // Set up filter group elements
    const details = clone.querySelector("details");
    const labelElement = clone.querySelector("[data-filter-label]");
    const valuesContainer = clone.querySelector("[data-filter-values]");
    const selectedCountElement = clone.querySelector("[data-selected-count]");

    if (details) {
      const ctxPrefix = this.config.contextPrefix || "filters";
      const identifier = this.config.identifier || "filter-drawer";
      const displayLabel = nostoTransformFilterLabel(filter.label);
      details.id = `filter-group--${ctxPrefix}--${identifier}--${handleize(displayLabel)}`;
      details.setAttribute("data-facet", filter.paramName);
      details.setAttribute("data-facet-label", displayLabel);
      details.setAttribute("data-index", container.children.length + 1);
    }

    if (labelElement) {
      // Use the transformed label for display
      const displayLabel = nostoTransformFilterLabel(filter.label);
      labelElement.textContent = displayLabel;
    }

    if (
      selectedCountElement &&
      filter.activeValues &&
      filter.activeValues.length > 0
    ) {
      const count = filter.activeValues.length;
      selectedCountElement.textContent =
        window.theme?.locales?.filtersSelected?.replace(
          "{{ count }}",
          count
        ) || `${count} selected`;
      selectedCountElement.setAttribute("data-selected-count", count);
      selectedCountElement.style.display = "";
    }

    if (valuesContainer && filter.values) {
      // Sort filter values based on filterValueOrder configuration
      const sortedValues = this.sortFilterValues(filter, filter.values);

      sortedValues.forEach((value) => {
        this.renderFilterValue(valuesContainer, filter, value);
      });
    }

    container.appendChild(clone);
  }

  /**
   * Sort filter values based on filterValueOrder configuration
   * @param {Object} filter - The filter object containing label and other data
   * @param {Array} values - Array of filter values to sort
   * @returns {Array} Sorted array of filter values
   * @private
   */
  sortFilterValues(filter, values) {
    if (!this.config.filterValueOrder || !filter.label) {
      return values; // Return original order if no configuration or filter label
    }

    // Transform the filter label for consistent naming and normalize to lowercase
    const transformedLabel = nostoTransformFilterLabel(filter.label);
    const normalizedFilterName = transformedLabel.toLowerCase();

    // Look for matching entry in filterValueOrder
    const orderConfig = this.config.filterValueOrder[normalizedFilterName];

    if (!orderConfig || !Array.isArray(orderConfig)) {
      if (this.allowDebug()) {
        console.log(`No filterValueOrder configuration found for "${normalizedFilterName}"`);
      }
      return values; // Return original order if no configuration found
    }

    if (this.allowDebug()) {
      console.log(`Sorting filter values for "${normalizedFilterName}" using order:`, orderConfig);
      console.log('Original values:', values.map(v => v.label || v.value));
    }

    // Create a map for quick lookup of order positions
    const orderMap = new Map();
    orderConfig.forEach((orderValue, index) => {
      orderMap.set(orderValue, index);
    });

    // Sort the values based on the configured order
    const sortedValues = [...values].sort((a, b) => {
      const aLabel = a.label || a.value;
      const bLabel = b.label || b.value;

      const aOrder = orderMap.has(aLabel) ? orderMap.get(aLabel) : Infinity;
      const bOrder = orderMap.has(bLabel) ? orderMap.get(bLabel) : Infinity;

      // If both have order positions, sort by position
      if (aOrder !== Infinity && bOrder !== Infinity) {
        return aOrder - bOrder;
      }

      // If only one has an order position, prioritize it
      if (aOrder !== Infinity) return -1;
      if (bOrder !== Infinity) return 1;

      // If neither has an order position, maintain original relative order
      // by comparing their original indices
      const aOriginalIndex = values.indexOf(a);
      const bOriginalIndex = values.indexOf(b);
      return aOriginalIndex - bOriginalIndex;
    });

    if (this.allowDebug()) {
      console.log('Sorted values:', sortedValues.map(v => v.label || v.value));
    }

    return sortedValues;
  }

  /**
   * Render filter value
   * @param {HTMLElement} container
   * @param {Object} filter
   * @param {Object} value
   * @private
   */
  renderFilterValue(container, filter, value) {
    const template = this.elements.templates.filterValue;
    const clone = template.content.cloneNode(true);

    const input = clone.querySelector("[data-filter-input]");
    const label = clone.querySelector("[data-filter-value-label]");
    const textElement = clone.querySelector("[data-filter-value-text]");
    const colorSwatch = clone.querySelector("[data-color-swatch]");

    // Check for special availability filter
    const isInstockParam =
      value.paramName === "filter.v.m.greyson.availability" &&
      value.value === "1";

    if (input) {
      const ctxPrefix = this.config.contextPrefix || "filters";
      const identifier = this.config.identifier || "filter-drawer";
      input.type = "checkbox";
      input.name = value.paramName || filter.paramName;
      input.value = value.value;
      input.id = `Filter--${ctxPrefix}--${identifier}--${filter.paramName.replace(/[^a-zA-Z0-9]/g, "-")}-${
        container.children.length + 1
      }`;
      input.checked = value.active || isInstockParam;
      input.disabled = value.count === 0 && !value.active;
      input.setAttribute("data-value", value.value.toLowerCase());

      // Store filter data for event handling
      input.dataset.filterName = filter.paramName;
      input.dataset.filterValue = value.value;
      this.attachFilterInputListener(input);
    }

    if (label) {
      label.setAttribute("for", input?.id);
      label.setAttribute(
        "data-extra-info",
        `${value.count || 0}-${value.active || false}`
      );

      if (value.active || isInstockParam) {
        label.classList.add("active");
      }
      if (value.count === 0 && !value.active) {
        label.classList.add("facet-checkbox--disabled");
      }
    }

    if (textElement) {
      textElement.textContent = value.label;
    }

    // Handle color swatches for Color Family filters
    if (
      colorSwatch &&
      (filter.label === "Color Family" ||
        filter.label.toLowerCase().includes("color"))
    ) {
      const swatchClass = `swatch--${value.label
        .toLowerCase()
        .replace(/[^a-zA-Z0-9]/g, "-")
        .replace("/", "-")}`;
      colorSwatch.className = `filter-color-option ${swatchClass}`;
      colorSwatch.style.display = "";

      // Hide checkbox icons for color filters
      const checkboxIcon = clone.querySelector(".icon-checkbox");
      const checkmarkIcon = clone.querySelector(".icon-checkmark-3");
      if (checkboxIcon) checkboxIcon.style.display = "none";
      if (checkmarkIcon) checkmarkIcon.style.display = "none";
    }

    container.appendChild(clone);
  }

  /**
   * Set up filter event listeners
   * @param {DocumentFragment} templateClone
   * @private
   */
  setupFilterEvents(templateClone) {
    // Sort change events
    const sortInputs = templateClone.querySelectorAll("[data-sort-input]");
    sortInputs.forEach((input) => {
      input.addEventListener("change", (event) => {
        if (event.target.checked) {
          this.handleSortChange(event.target.value);
        }
      });
    });

    // Filter change events
    const filterInputs = templateClone.querySelectorAll("[data-filter-input]");
    filterInputs.forEach((input) => {
      this.attachFilterInputListener(input);
    });

    // Clear all events
    const clearAllButtons = templateClone.querySelectorAll(
      "[data-clear-all], [data-clear-all-mobile]"
    );
    clearAllButtons.forEach((button) => {
      const clearAllUrl = this.buildClearAllFiltersUrl();
      button.href = clearAllUrl;

      button.addEventListener("click", (event) => {
        event.preventDefault();
        this.clearAllFilters();
      });
    });
  }

  /**
   * Attach a filter input listener once per element.
   * @param {HTMLInputElement} input
   * @private
   */
  attachFilterInputListener(input) {
    if (!input || input.dataset.filterListenerAttached === "true") {
      return;
    }

    input.addEventListener("change", (event) => {
      const filterName = event.target.dataset.filterName;
      const filterValue = event.target.dataset.filterValue;
      const action = event.target.checked ? "add" : "remove";

      this.handleFilterChange(filterName, filterValue, action);
    });

    input.dataset.filterListenerAttached = "true";
  }

  /**
   * Handle filter change
   * @param {string} filterName
   * @param {string} filterValue
   * @param {string} action
   * @private
   */
  handleFilterChange(filterName, filterValue, action) {
    if (this.allowDebug()) {
      console.log("handleFilterChange", { filterName, filterValue, action });
    }

    if (!this.state.activeFilters) {
      this.state.activeFilters = {};
    }

    if (!this.state.activeFilters[filterName]) {
      this.state.activeFilters[filterName] = [];
    }

    if (action === "add") {
      if (!this.state.activeFilters[filterName].includes(filterValue)) {
        this.state.activeFilters[filterName].push(filterValue);
      }
    } else if (action === "remove") {
      this.state.activeFilters[filterName] = this.state.activeFilters[
        filterName
      ].filter((value) => value !== filterValue);

      // Remove empty filter arrays
      if (this.state.activeFilters[filterName].length === 0) {
        this.state.activeFilters[filterName] = undefined;
      }
    }

    // Update active filters state immediately
    this.updateActiveFiltersState();

    // Sync with featured filters
    this.syncWithFeaturedFilters();

    // Notify product grids about filter change
    this.dispatchFilterChangeEvent(filterName, filterValue, action);
  }

  /**
   * Handle sort change
   * @param {string} sortBy
   * @private
   */
  handleSortChange(sortBy) {
    if (this.allowDebug()) {
      console.log("handleSortChange", { sortBy });
    }

    this.config.sortBy = sortBy;

    // Update the active sort display text
    this.updateActiveSortDisplay(sortBy);

    // Notify product grids about sort change
    this.dispatchSortChangeEvent(sortBy);
  }

  /**
   * Update the active sort display text
   * @param {string} sortBy
   * @private
   */
  updateActiveSortDisplay(sortBy) {
    const activeSortElement = this.querySelector("[data-active-sort]");
    if (!activeSortElement) return;

    let activeSortLabel = "Relevance";
    if (sortBy === "manual") {
      activeSortLabel = "Featured";
    } else if (sortBy === "best-selling") {
      activeSortLabel = "Best Selling";
    } else if (sortBy === "price-ascending") {
      activeSortLabel = "Price - Low To High";
    } else if (sortBy === "price-descending") {
      activeSortLabel = "Price - High To Low";
    } else {
      const sortOptions = this.getSortOptions();
      const activeOption = sortOptions.find(([value]) => value === sortBy);
      if (activeOption) {
        activeSortLabel = activeOption[1];
      }
    }

    activeSortElement.textContent = activeSortLabel;
  }

    /**
   * Clear all filters
   * @private
   */
  clearAllFilters() {
    const vendor = window.theme?.data?.source || "shopify";
    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    // For Shopify, preserve availability filters when clearing all filters (maintains product visibility)
    if (vendor === "shopify") {
      const preservedFilters = {};
      Object.entries(this.state.activeFilters || {}).forEach(([key, values]) => {
        if (availabilityFilters.includes(key)) {
          preservedFilters[key] = values;
        }
      });
      this.state.activeFilters = preservedFilters;
    } else {
      // For Nosto, availability is handled in preFilter, so clear everything
      this.state.activeFilters = {};
    }

    // Update the UI to reflect cleared filters
    this.updateFilterDrawerUI();

    // Sync with featured filters
    this.syncWithFeaturedFilters();

    // Dispatch event to notify product grid (which will handle URL history update)
    this.dispatchClearFiltersEvent();
  }

  /**
   * Dispatch filter change event
   * @param {string} filterName
   * @param {string} filterValue
   * @param {string} action
   * @private
   */
  dispatchFilterChangeEvent(filterName, filterValue, action) {
    const event = new CustomEvent("filter-drawer:filter-change", {
      detail: {
        filterName,
        filterValue,
        action,
        identifier: this.config.gridId || this.config.identifier || null
      },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Dispatch sort change event
   * @param {string} sortBy
   * @private
   */
  dispatchSortChangeEvent(sortBy) {
    const event = new CustomEvent("filter-drawer:sort-change", {
      detail: {
        sortBy,
        identifier: this.config.gridId || this.config.identifier || null
      },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Dispatch clear filters event
   * @private
   */
  dispatchClearFiltersEvent() {
    const event = new CustomEvent("filter-drawer:clear-filters", {
      detail: {
        identifier: this.config.gridId || this.config.identifier || null
      },
      bubbles: true,
    });
    document.dispatchEvent(event);
  }

  /**
   * Update component state
   * @param {Object} newState
   * @private
   */
  setState(newState) {
    this.state = { ...this.state, ...newState };
  }

  /**
   * Show loading state
   * @private
   */
  showLoadingState() {
    if (!this.elements.filtersContainer) return;

    this.elements.filtersContainer.dataset.filterState = "loading";

    // Use CSS classes instead of inline styles
    if (this.elements.loadingState) {
      this.elements.loadingState.removeAttribute("style");
      this.elements.loadingState.classList.add("visible");
    }

    if (this.elements.errorState) {
      this.elements.errorState.removeAttribute("style");
      this.elements.errorState.classList.remove("visible");
    }

    if(this.allowDebug()){
      console.log("Filter drawer state set to loading");
    }
  }

  /**
   * Show error state
   * @param {string} message
   * @private
   */
  showErrorState(message) {
    if (!this.elements.filtersContainer) return;

    this.elements.filtersContainer.dataset.filterState = "error";

    // Use CSS classes instead of inline styles
    if (this.elements.loadingState) {
      this.elements.loadingState.removeAttribute("style");
      this.elements.loadingState.classList.remove("visible");
    }

    if (this.elements.errorState) {
      this.elements.errorState.removeAttribute("style");
      this.elements.errorState.classList.add("visible");

      // Update error message if provided
      if (message) {
        const messageElement = this.elements.errorState.querySelector("p");
        if (messageElement) {
          messageElement.textContent = message;
        }
      }
    }

    if(this.allowDebug()){
      console.log("Filter drawer state set to error:", message);
    }
  }

  /**
   * Set loaded state
   * @private
   */
  setLoadedState() {
    if (!this.elements.filtersContainer) return;

    this.elements.filtersContainer.dataset.filterState = "loaded";

    // Use CSS classes instead of inline styles
    if (this.elements.loadingState) {
      this.elements.loadingState.removeAttribute("style");
      this.elements.loadingState.classList.remove("visible");
    }

    if (this.elements.errorState) {
      this.elements.errorState.removeAttribute("style");
      this.elements.errorState.classList.remove("visible");
    }

    if (this.allowDebug()) {
      console.log("Filter drawer state set to loaded");
    }
  }

  /**
   * Build clear all filters URL
   * @returns {string}
   * @private
   */
  buildClearAllFiltersUrl() {
    const currentUrl = new URL(window.location);
    const params = new URLSearchParams();

    if (this.config.context === "search" && this.config.terms) {
      params.set("q", this.config.terms);
      if (this.config.type) {
        params.set("type", this.config.type);
      }
    }

    // Always maintain the availability filter
    params.set("filter.v.m.greyson.availability", "1");

    return `${currentUrl.pathname}?${params.toString()}`;
  }

  /**
   * Get display title for current context
   * @returns {string}
   * @private
   */
  getDisplayTitle() {
    return decodeHTMLEntities(this.config.drawerTitle);
  }

  /**
   * Initialize active filters from current URL parameters
   * @private
   */
  initActiveFiltersFromUrl() {
    // Only read from URL if leverageUrl is enabled
    if (this.config.leverageUrl === false) {
      this.state.activeFilters = {};
      if (this.allowDebug()) {
        console.log("FilterDrawer: leverageUrl is false, not reading filters from URL");
      }
      return;
    }

    // Use shared utility function to parse URL parameters
    this.state.activeFilters = parseUrlParametersToActiveFilters(null, {
      debug: this.allowDebug()
    });

    if (this.allowDebug()) {
      console.log(
        "FilterDrawer initialized activeFilters from URL:",
        this.state.activeFilters
      );
    }
  }

  /**
   * Update the filter drawer UI to reflect current active filters state
   * @private
   */
  updateFilterDrawerUI() {
    const container = this.elements.filtersContainer;
    if (!container) return;

    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    // Update all filter checkboxes to reflect current state
    const filterInputs = container.querySelectorAll("[data-filter-input]");
    filterInputs.forEach((input) => {
      const filterName = input.dataset.filterName;
      const filterValue = input.dataset.filterValue;

      // Check if this filter should be active
      let shouldBeChecked = false;

      // Check active filters state
      if (this.state.activeFilters && this.state.activeFilters[filterName]) {
        shouldBeChecked = this.state.activeFilters[filterName].includes(filterValue);
      }

      // Special handling for availability filters
      if (filterName === "filter.v.m.greyson.availability" && filterValue === "1") {
        shouldBeChecked = true; // Always keep availability filter checked
      }

      // Update checkbox state
      input.checked = shouldBeChecked;

      // Update label styling
      const label = input.closest('label') || container.querySelector(`label[for="${input.id}"]`);
      if (label) {
        if (shouldBeChecked) {
          label.classList.add("active");
        } else {
          label.classList.remove("active");
        }
      }
    });

    // Update selected count displays for each filter group
    const filterGroups = container.querySelectorAll("details[data-facet]");
    filterGroups.forEach((group) => {
      const selectedCountElement = group.querySelector("[data-selected-count]");
      if (selectedCountElement) {
        const groupInputs = group.querySelectorAll("[data-filter-input]:checked");
        let activeCount = 0;

        // Count only non-availability filters
        groupInputs.forEach((input) => {
          const filterName = input.dataset.filterName;
          if (!availabilityFilters.includes(filterName)) {
            activeCount++;
          }
        });

        if (activeCount > 0) {
          selectedCountElement.textContent =
            window.theme?.locales?.filters_selected?.replace(
              "{{ count }}",
              activeCount
            ) || `${activeCount} selected`;
          selectedCountElement.setAttribute("data-selected-count", activeCount);
          selectedCountElement.style.display = "";
        } else {
          selectedCountElement.style.display = "none";
        }
      }
    });

    // Update overall active filters state
    this.updateActiveFiltersState();
  }

  /**
   * Update the active filters state and total count display
   * @private
   */
  updateActiveFiltersState() {
    // Count active filters from current state, excluding availability filters
    let totalActiveFilters = 0;
    const availabilityFilters = [
      "filter.p.m.greyson.availability",
      "filter.v.m.greyson.availability"
    ];

    // Only use URL parameters for counting if leverageUrl is enabled
    if (this.config.leverageUrl !== false) {
      // Use URL parameters as the single source of truth for counting active filters
      // This prevents double-counting since internal state mirrors URL parameters
      const urlParams = new URLSearchParams(window.location.search);
      for (const key of urlParams.keys()) {
        if (key.startsWith("filter.") && !availabilityFilters.includes(key)) {
          totalActiveFilters++;
        }
      }
    } else {
      // When not leveraging URL, count from internal state only
      if (this.state.activeFilters) {
        Object.entries(this.state.activeFilters).forEach(([key, values]) => {
          if (!availabilityFilters.includes(key) && values && values.length > 0) {
            totalActiveFilters += values.length;
          }
        });
      }
    }

    // Set data attribute on filter drawer element for CSS to handle visibility
    this.dataset.activeFilters = totalActiveFilters > 0 ? "true" : "false";

    // Update total selected display text
    const container = this.elements.filtersContainer;
    if (container) {
      const totalSelectedElement = container.querySelector(
        "[data-total-selected]"
      );
      if (totalSelectedElement && totalActiveFilters > 0) {
        totalSelectedElement.textContent =
          window.theme?.locales?.filters_selected?.replace(
            "{{ count }}",
            totalActiveFilters
          ) || `${totalActiveFilters} selected`;
      }
    }

    if (this.allowDebug()) {
      console.log("Updated active filters state:", {
        totalActiveFilters,
        dataAttribute: this.dataset.activeFilters,
        leverageUrl: this.config.leverageUrl,
      });
    }

    // Sync with featured filters whenever active filters state changes
    this.syncWithFeaturedFilters();
  }

  /**
   * Re-enable a filter group when it has available values
   * @param {HTMLElement} groupElement - The filter group DOM element
   * @private
   */
  enableFilterGroup(groupElement) {
    // Remove disabled class from the filter label
    const filterLabel = groupElement.querySelector("[data-filter-label]");
    if (filterLabel) {
      filterLabel.classList.remove("facet-checkbox--disabled");
      filterLabel.style.opacity = "";
    }

    // Show the arrow SVG
    const arrowSvg = groupElement.querySelector("summary svg");
    if (arrowSvg) {
      arrowSvg.style.display = "";
    }

    // Allow the details element to open
    groupElement.style.pointerEvents = "";

    // Remove disabled class
    groupElement.classList.remove("facets__disclosure--disabled");

    if (this.allowDebug()) {
      console.log(`Re-enabled filter group: ${groupElement.getAttribute("data-facet")}`);
    }
  }

  /**
   * Sync with featured filters to keep button states in sync
   * @private
   */
  syncWithFeaturedFilters() {
    const featuredFilters = document.querySelector('featured-filters');
    if (featuredFilters && featuredFilters.updateButtonStates) {
      if (this.allowDebug()) {
        console.log('FilterDrawer: Syncing with featured filters', this.state.activeFilters);
      }
      featuredFilters.updateButtonStates(this.state.activeFilters);
    }
  }

  /**
   * Initialize featured filters sync when drawer is first rendered
   * @private
   */
  initFeaturedFiltersSync() {
    // Listen for featured filter changes that should update the drawer
    document.addEventListener('featured-filters:filter-change', (event) => {
      const { filterName, filterValue, action } = event.detail;

      if (this.allowDebug()) {
        console.log('FilterDrawer: Received featured filter change', { filterName, filterValue, action });
      }

      // Handle the filter change
      this.handleFilterChange(filterName, filterValue, action);
    });

    // Initial sync if we have active filters
    if (this.state.activeFilters && Object.keys(this.state.activeFilters).length > 0) {
      this.syncWithFeaturedFilters();
    }
  }

  /**
   * Set up close button event listeners after filters are rendered
   * This is needed because close buttons are dynamically added to DOM
   * @private
   */
  setupCloseButtonListeners() {
    const closeButtons = this.querySelectorAll('.slide-out-drawer__close');

    closeButtons.forEach(button => {
      // Remove any existing listeners to prevent duplicates
      button.removeEventListener('click', this.close);
      // Add the close listener
      button.addEventListener('click', this.close.bind(this));

      // Handle keyboard events for span elements with role="button"
      if (button.tagName === 'SPAN' && button.getAttribute('role') === 'button') {
        button.removeEventListener('keydown', this.handleCloseKeydown);
        button.addEventListener('keydown', this.handleCloseKeydown.bind(this));
      }
    });

    if (this.allowDebug() && closeButtons.length > 0) {
      console.log(`FilterDrawer: Set up ${closeButtons.length} close button listeners`);
    }
  }

  /**
   * Handle keyboard events for close buttons
   * @param {KeyboardEvent} event
   * @private
   */
  handleCloseKeydown(event) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.close();
    }
  }
}

// Register the custom element
customElements.define("filter-drawer", FilterDrawer);
